<template>
  <div>
    <div class="share-button-container container background-container mt-4">
      <MainBanner />
      <ShareButton />
    </div>
    <div class="page container">
      <Carousel
        id="trendingCreatorsDiscovery"
        class="mt-5"
        :items="trendingCreators"
        @click="handleCreatorClick"
      >
        <template #title>
          <h2>Trending creators</h2>
        </template>

        <template #itemTitle="{ item }">
          <div v-if="item" class="text-overflow">
            @{{ $get(item, 'profile.handle') }}
          </div>
        </template>

        <template #itemSubtitle="{ item }">
          <span v-if="item">{{ $get(item, 'profile.tagline') }} </span>
        </template>
      </Carousel>

      <RecipeGrid
        class="mt-4"
        title="Compliments to the chefs"
        :recipes="trendingRecipes"
        :count-items="trendingRecipeCount"
        @click="handleRecipeClick"
      />
    </div>

    <TitleWithBackground
      :lines="['Publishing the future.', 'by anyone, for everyone']"
    />

    <div class="page container">
      <h2 class="mt-4">Fresh from the creators</h2>

      <div class="creator__spotlights mt-4">
        <CreatorSpotlight
          v-for="(creator, index) in randomCreators"
          :key="creator._id"
          :creator="creator"
          :background="spotlightBackgrounds[index]"
          @click="handleCreatorClick"
        />
      </div>

      <CreatorCarousel
        id="discoverCreators"
        class="mt-4"
        :items="latestCreators"
        @click="handleCreatorClick"
      >
        <template #title>
          <h2>Discover creators</h2>
        </template>
      </CreatorCarousel>

      <Carousel
        id="recommendedProducts"
        class="mt-5"
        :items="recommendedProducts"
        :mode="CarouselModes.PRODUCT"
        show-default-item-actions
        @click="handleProductClick"
      >
        <template #title>
          <h2>Discover products</h2>
        </template>
      </Carousel>

      <TrendingBlock v-if="trendingFeed" class="mt-5" :items="trendingFeed" />
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import getRandomBackgrounds from '../../checkoutForm/lib/getRandomBackgrounds';
import { CarouselModes } from '@/misc/constants';

import ShareButton from '@/components/Elements/ShareButton';
import MainBanner from '@/components/MainBanner/Index';
import TitleWithBackground from '@/components/TitleWithBackground';

import {
  fetchCreatorsTrending,
  fetchCreatorsLatest,
  fetchProductsLatest
} from '@/services/statistics';
import { fetchTrendingRecipes } from '~/services/recipes';
import { fetchTrendingFeed } from '@/services/selections';
import formatMoney from '@/misc/formatMoney';
import getRandomCreators from '@/misc/getRandomCreators';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import getFormatPrice from '@/misc/getFormatPrice';
import filterProductsByRegion from '@/misc/filterProductByRegion';

export default {
  name: 'IndexPage',

  components: {
    ShareButton,
    MainBanner,
    TitleWithBackground,
    TrendingBlock: () =>
      import(/* webpackPrefetch: true */ '@/components/TrendingBlock/Index'),
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index'),
    CreatorCarousel: () =>
      import(/* webpackPrefetch: true */ '@/components/CreatorCarousel'),
    CreatorSpotlight: () =>
      import(/* webpackPrefetch: true */ '@/components/CreatorSpotlight'),
    RecipeGrid: () =>
      import(/* webpackPrefetch: true */ '@/components/RecipeGrid/Index')
  },

  asyncData({ $axios, error, store }) {
    return Promise.all([
      fetchCreatorsTrending($axios),
      fetchProductsLatest($axios),
      fetchTrendingRecipes($axios),
      fetchCreatorsLatest($axios)
    ])
      .then((values) => {
        return {
          trendingCreators: get(values, '[0].data'),
          randomCreators: getRandomCreators(get(values, '[0].data'), 2),
          recommendedProducts: filterProductsByRegion(
            get(values, '[1].data'),
            store.getters['config/region']
          ),
          trendingRecipes: get(values, '[2].data'),
          latestCreators: get(values, '[3].data')
        };
      })
      .catch(() => {
        error({ statusCode: 500 });
        return {
          recommendedProducts: [],
          trendingCreators: [],
          trendingRecipes: [],
          randomCreators: [],
          latestCreators: []
        };
      });
  },

  data() {
    return {
      CarouselModes,
      buyNowLoading: false,
      trendingFeed: null,
      spotlightBackgrounds: []
    };
  },

  head: {
    title: `Welcome to Found! Discover today's social creators`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Found: Publishing creative content by anyone, for everyone. Discover the top social stars and get creative with their recipes, books and more!'
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: require('@/assets/logo.svg')
      },
      {
        hid: 'twitter:image',
        name: 'twitter:image',
        content: require('@/assets/logo.svg')
      }
    ]
  },

  computed: {
    ...mapGetters({
      currency: 'config/currency',
      isMobile: 'main/isMobile',
      region: 'config/region'
    }),
    trendingRecipeCount() {
      if (process.client) {
        return this.isMobile ? 6 : 9;
      }
      return null;
    }
  },

  mounted() {
    this.getSpotlightBackgrounds();
    fetchTrendingFeed(this.$axios).then((trendingFeed) => {
      this.trendingFeed = trendingFeed;
    });
  },

  methods: {
    handleProductClick(params) {
      this.$router.push({ path: `products/${this.$get(params, 'item.id')}` });
    },
    handleCreatorClick(creator) {
      this.$router.push({
        path: `creators/${
          this.$get(creator, 'profile.handle') ||
          this.$get(creator, 'item.profile.handle') ||
          this.$get(creator, 'id')
        }`
      });
    },
    handleRecipeClick(recipe) {
      this.$router.push({
        path: `recipe/${this.$get(recipe, '_id')}`
      });
    },
    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },
    getSpotlightBackgrounds() {
      this.spotlightBackgrounds = getRandomBackgrounds(this.randomCreators);
    }
  }
};
</script>

<style lang="scss" scoped>
.creator__spotlights {
  display: flex;
  flex-direction: column;

  @media (min-width: $screen-width-lg) {
    flex-direction: row;
    justify-content: space-between;
  }
}

.red-title {
  color: $red-color;
}
</style>
